import React, { useState, useEffect, useCallback } from "react";
import { useStreaming } from "../context/StreamingContext";
import "./CommonOrdersPanel.css";

interface OrderItem {
  itemid: string;
  description: string;
  patientrate: string;
  encounterrate: string;
  npatientscohortitem: number;
  nencounterscohortitem: number;
  npatientscohorttotal: number;
  nencounterscohorttotal: number;
  result_type: string;
}

interface OrderSection {
  type: "lab" | "med" | "procedure";
  title: string;
  data: OrderItem[];
  isLoading: boolean;
  isExpanded: boolean;
}

interface CommonOrdersPanelProps {
  onQuestionClick?: (question: string) => void;
}

const CommonOrdersPanel: React.FC<CommonOrdersPanelProps> = ({
  onQuestionClick,
}) => {
  const [isOpen, setIsOpen] = useState(true);
  const [activeTab, setActiveTab] = useState<"lab" | "med" | "procedure">(
    "lab",
  );
  const { clinicalCaseData } = useStreaming();

  const [labSection, setLabSection] = useState<OrderSection>({
    type: "lab",
    title: "Labs",
    data: [],
    isLoading: false,
    isExpanded: true,
  });

  const [medSection, setMedSection] = useState<OrderSection>({
    type: "med",
    title: "Medicine",
    data: [],
    isLoading: false,
    isExpanded: true,
  });

  const [procedureSection, setProcedureSection] = useState<OrderSection>({
    type: "procedure",
    title: "Imaging",
    data: [],
    isLoading: false,
    isExpanded: true,
  });

  const fetchOrders = useCallback(
    async (resultType: "lab" | "med" | "procedure") => {
      if (!clinicalCaseData) return;

      // Set loading state
      const setSection =
        resultType === "lab"
          ? setLabSection
          : resultType === "med"
            ? setMedSection
            : setProcedureSection;
      setSection((prev) => ({ ...prev, isLoading: true }));

      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/get_orders`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            credentials: "include",
            body: JSON.stringify({
              icd10_code: clinicalCaseData.icd10_code,
              patient_age: clinicalCaseData.patient_age,
              patient_gender: clinicalCaseData.patient_gender,
              result_type: resultType,
              limit: 10,
              min_patients_for_non_rare_items: 1,
              year: 2024,
            }),
          },
        );

        if (response.ok) {
          const responseData = await response.json();
          console.log(`${resultType} orders:`, responseData);

          // Extract the data array from the response
          const ordersData = responseData.data || [];
          setSection((prev) => ({
            ...prev,
            data: ordersData,
            isLoading: false,
          }));
        } else {
          console.error(
            `Failed to fetch ${resultType} orders:`,
            response.status,
          );
          setSection((prev) => ({ ...prev, isLoading: false }));
        }
      } catch (error) {
        console.error(`Error fetching ${resultType} orders:`, error);
        setSection((prev) => ({ ...prev, isLoading: false }));
      }
    },
    [clinicalCaseData],
  );

  // Fetch orders when clinical case data is available
  useEffect(() => {
    if (
      clinicalCaseData &&
      clinicalCaseData.icd10_code &&
      clinicalCaseData.patient_age &&
      clinicalCaseData.patient_gender
    ) {
      fetchOrders("lab");
      fetchOrders("med");
      fetchOrders("procedure");
    }
  }, [clinicalCaseData, fetchOrders]);

  const getCurrentSection = () => {
    if (activeTab === "lab") return labSection;
    if (activeTab === "med") return medSection;
    return procedureSection;
  };

  const getColorForIndex = (index: number): string => {
    const colors = ["#4A5D7C", "#C67B3E", "#7B8F6C", "#006241", "#5B7B8F"];
    return colors[index % colors.length];
  };

  const [expandedOrders, setExpandedOrders] = useState<Record<string, boolean>>(
    {},
  );

  const toggleOrderExpansion = (orderId: string) => {
    setExpandedOrders((prev) => ({
      ...prev,
      [orderId]: !prev[orderId],
    }));
  };

  const renderTabContent = (section: OrderSection) => {
    return (
      <div className="tab-content">
        {section.isLoading ? (
          // Show skeleton loaders
          <div className="orders-list">
            {[1, 2, 3].map((index) => (
              <div key={index} className="order-item skeleton-order">
                <div
                  className="skeleton-line"
                  style={{ width: "48px", height: "48px", borderRadius: "8px" }}
                ></div>
                <div style={{ flex: 1 }}>
                  <div
                    className="skeleton-line"
                    style={{
                      width: "70%",
                      height: "14px",
                      marginBottom: "8px",
                    }}
                  ></div>
                  <div
                    className="skeleton-line"
                    style={{ width: "50%", height: "12px" }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        ) : section.data.length > 0 ? (
          // Show actual orders
          <div className="orders-list">
            {section.data.map((order, index) => {
              const orderId = `${section.type}-${order.itemid}`;
              const isExpanded = expandedOrders[orderId];

              return (
                <div key={orderId} className="order-card">
                  <div
                    className="order-card-header"
                    onClick={() => toggleOrderExpansion(orderId)}
                  >
                    <div className="order-card-main">
                      <div
                        className="order-icon"
                        style={{ backgroundColor: getColorForIndex(index) }}
                      >
                        <svg
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <rect
                            x="6"
                            y="4"
                            width="12"
                            height="16"
                            rx="2"
                            fill="white"
                            opacity="0.9"
                          />
                          <rect
                            x="8"
                            y="8"
                            width="8"
                            height="2"
                            fill={getColorForIndex(index)}
                          />
                          <rect
                            x="8"
                            y="12"
                            width="6"
                            height="2"
                            fill={getColorForIndex(index)}
                          />
                        </svg>
                      </div>
                      <div className="order-card-info">
                        <h4 className="order-description">
                          {order.description}
                        </h4>
                        <p className="order-itemid">ID: {order.itemid}</p>
                      </div>
                    </div>
                    <button
                      className="expand-order-button"
                      aria-label={isExpanded ? "Collapse" : "Expand"}
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleOrderExpansion(orderId);
                      }}
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className={`order-chevron ${isExpanded ? "expanded" : ""}`}
                      >
                        <path
                          d="M4 6L8 10L12 6"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  </div>

                  {isExpanded && (
                    <div className="order-card-details">
                      <div className="order-detail-row">
                        <span className="detail-label">Patient Rate:</span>
                        <span className="detail-value">
                          {order.patientrate}%
                        </span>
                      </div>
                      <div className="order-detail-row">
                        <span className="detail-label">Encounter Rate:</span>
                        <span className="detail-value">
                          {order.encounterrate}%
                        </span>
                      </div>
                      <div className="order-detail-row">
                        <span className="detail-label">Patients (Item):</span>
                        <span className="detail-value">
                          {order.npatientscohortitem}
                        </span>
                      </div>
                      <div className="order-detail-row">
                        <span className="detail-label">Encounters (Item):</span>
                        <span className="detail-value">
                          {order.nencounterscohortitem}
                        </span>
                      </div>
                      <div className="order-detail-row">
                        <span className="detail-label">Total Patients:</span>
                        <span className="detail-value">
                          {order.npatientscohorttotal}
                        </span>
                      </div>
                      <div className="order-detail-row">
                        <span className="detail-label">Total Encounters:</span>
                        <span className="detail-value">
                          {order.nencounterscohorttotal}
                        </span>
                      </div>
                      {/* <button className="add-to-cart-button">
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 16 16"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <circle
                                cx="8"
                                cy="8"
                                r="7"
                                stroke="currentColor"
                                strokeWidth="1.5"
                              />
                              <path
                                d="M8 5v6M5 8h6"
                                stroke="currentColor"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                              />
                            </svg>
                            Add to Cart
                          </button> */}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="no-orders">No orders available</div>
        )}
      </div>
    );
  };

  // Removed related questions logic as it moved to chat section

  return (
    <div className="aside-panel">
      {/* {isOpen ? (
        <div className="common-orders-panel">
          <div className="panel-header">
            <div className="panel-title">
              <img
                src="/ai-chat-icon.png"
                alt="AI Icon"
                width="40"
                height="35"
                className="panel-icon"
              />
              <h3>Common Orders for This Condition</h3>
            </div>
            <button
              className="close-button"
              onClick={() => setIsOpen(false)}
              aria-label="Close panel"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M12 4L4 12M4 4l8 8"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>

          <div className="tabs-container">
            <div className="tabs">
              <button
                className={`tab ${activeTab === "lab" ? "active" : ""}`}
                onClick={() => setActiveTab("lab")}
              >
                {labSection.title}
              </button>
              <button
                className={`tab ${activeTab === "procedure" ? "active" : ""}`}
                onClick={() => setActiveTab("procedure")}
              >
                {procedureSection.title}
              </button>
              <button
                className={`tab ${activeTab === "med" ? "active" : ""}`}
                onClick={() => setActiveTab("med")}
              >
                {medSection.title}
              </button>
            </div>
            {renderTabContent(getCurrentSection())}
          </div>
        </div>
      ) : (
        <div className="common-orders-panel collapsed">
          <button className="reopen-button" onClick={() => setIsOpen(true)}>
            <img
              src="/ai-chat-icon.png"
              alt="AI Icon"
              width="20"
              height="20"
              className="panel-icon"
            />
            View Common Orders
          </button>
        </div>
      )} */}

      {/* Next Steps Section */}
      <div className="next-steps-panel">
        <div className="next-steps-header">
          <div className="sparkle-icon">
            <img src="/ai-chat-icon.png" alt="AI Icon" width="40" height="35" />
          </div>
          <h3>Next Steps</h3>
        </div>
        <div className="next-steps-list">
          {/* Medications */}
          {(medSection.data.length > 0
            ? medSection.data.slice(0, 3)
            : [1, 2, 3]
          ).map((item, idx) => (
            <div
              key={`next-med-${idx}`}
              className="next-step-item"
              onClick={() =>
                onQuestionClick?.(
                  `Tell me more about ${typeof item === "object" ? item.description : "Synthroid 0.15 µg/day"}`,
                )
              }
            >
              <div className="next-step-icon-container med">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="m10.5 20.5 10-10a4.95 4.95 0 1 0-7-7l-10 10a4.95 4.95 0 1 0 7 7Z"></path>
                  <path d="m8.5 8.5 7 7"></path>
                </svg>
              </div>
              <span className="next-step-label">
                {typeof item === "object"
                  ? item.description
                  : "Synthroid 0.15 µg/day"}
              </span>
            </div>
          ))}

          {/* Imaging */}
          {(procedureSection.data.length > 0
            ? procedureSection.data.slice(0, 3)
            : [1, 2, 3]
          ).map((item, idx) => (
            <div
              key={`next-proc-${idx}`}
              className="next-step-item"
              onClick={() =>
                onQuestionClick?.(
                  `Tell me more about ${typeof item === "object" ? item.description : "Novation X Ray"}`,
                )
              }
            >
              <div className="next-step-icon-container procedure">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.29 1.51 4.04 3 5.5l7 7z"></path>
                </svg>
              </div>
              <span className="next-step-label">
                {typeof item === "object" ? item.description : "Novation X Ray"}
              </span>
            </div>
          ))}

          {/* Labs */}
          {(labSection.data.length > 0
            ? labSection.data.slice(0, 3)
            : [1, 2, 3]
          ).map((item, idx) => (
            <div
              key={`next-lab-${idx}`}
              className="next-step-item"
              onClick={() =>
                onQuestionClick?.(
                  `Tell me more about ${typeof item === "object" ? item.description : "Total T3 Diagnostics"}`,
                )
              }
            >
              <div className="next-step-icon-container lab">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M9 2v3"></path>
                  <path d="M15 2v3"></path>
                  <path d="M9 22h6"></path>
                  <path d="M16 5c1.1 0 2 .9 2 2v2c0 1.1-.9 2-2 2H8c-1.1 0-2-.9-2-2V7c0-1.1.9-2 2-2h8Z"></path>
                  <path d="M6 9h12"></path>
                  <path d="M16 11l2 6v2c0 1.1-.9 2-2 2H8c-1.1 0-2-.9-2-2v-2l2-6"></path>
                </svg>
              </div>
              <span className="next-step-label">
                {typeof item === "object"
                  ? item.description
                  : "Total T3 Diagnostics"}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Fixed Footer Logo */}
      <div className="sidebar-footer">
        <img
          src="/standford medicine logo.png"
          alt="Stanford Medicine"
          className="footer-logo"
        />
      </div>
    </div>
  );
};

export default CommonOrdersPanel;
